import { Controller } from "@hotwired/stimulus"

// Native full-card snap, with Fleet's scrollend + held-gesture settlement contract.
// No frame loading, selection persistence or client-side economy belongs here.
export default class extends Controller {
  static targets = ["rail", "card"]

  get activeCard() { return this.cardTargets[this.index] }
  get confirmTarget() { return this.activeCard.querySelector(".ve-purchase-confirm") }
  get reasonTarget() { return this.activeCard.querySelector(".ve-purchase-reason") }
  get reconcileTarget() { return this.activeCard.querySelector(".ve-purchase-actions a") }

  connect() {
    this.index = Math.max(0, this.cardTargets.findIndex(card => card.querySelector("input").checked))
    this.scrollEndSupported = "onscrollend" in this.railTarget
    this.railTarget.scrollLeft = this.offsetFor(this.index)
    this.activate(this.index)
  }

  disconnect() { clearTimeout(this.scrollTimer) }

  submit(event) {
    if (this.submitting || this.moving || this.confirmTarget.disabled || event.submitter !== this.confirmTarget) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  pending() {
    this.submitting = true
    this.element.setAttribute("data-refresh-hold", "")
    this.confirmTarget.disabled = true
    this.activeCard.querySelectorAll(".ve-fleet-chev").forEach(button => { button.disabled = true })
    this.element.querySelector("fieldset").disabled = true
  }

  unknown(event) {
    event.preventDefault()
    event.stopPropagation()
    this.uncertain = true
    this.pending()
    this.reasonTarget.textContent = "Purchase status is unknown. Check ownership and balance before retrying."
    this.reconcileTarget.hidden = false
  }

  finished(event) {
    if (this.uncertain) return this.pending()
    this.submitting = false
    this.element.removeAttribute("data-refresh-hold")
    // Native tab routing retains Dashboard without a turbo:before-cache event.
    // Retire this form once Turbo hands a known response to page navigation;
    // otherwise the open dialog blocks ownership revalidation on tab return.
    if (event.detail.success) {
      this.dispatch("completed")
      return
    }
    this.element.querySelector("fieldset").disabled = false
    this.activate(this.index)
  }

  previous() { this.navigate(this.index - 1) }
  next() { this.navigate(this.index + 1) }

  navigate(index) {
    if (this.submitting || this.moving || !this.cardTargets[index]) return
    this.startMoving()
    this.railTarget.scrollTo({ left: this.offsetFor(index), behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" })
  }

  startMoving() {
    this.moving = true
    this.element.dataset.pickerMoving = ""
    this.cardTargets.forEach(card => { card.querySelector(".ve-purchase-confirm").disabled = true })
  }

  scroll() {
    if (this.submitting) {
      this.railTarget.scrollLeft = this.offsetFor(this.index)
      return
    }
    this.startMoving()
    this.scrollSinceInteraction = true
    this.scheduleFallbackSettle()
  }

  scrollEnd() {
    if (this.pointerActive || this.touchActive) {
      this.swallowedScrollEnd = true
      return
    }
    this.settle()
  }

  pointerDown() { this.beginInteraction(); this.pointerActive = true }
  pointerUp() { this.pointerActive = false; this.scheduleFallbackSettle() }
  pointerCancel() { this.pointerActive = false; this.scheduleFallbackSettle() }
  touchStart() { this.beginInteraction(); this.touchActive = true }
  touchEnd(event) { this.touchActive = (event.touches?.length || 0) > 0; this.scheduleFallbackSettle() }

  beginInteraction() {
    if (this.pointerActive || this.touchActive) return
    clearTimeout(this.scrollTimer)
    this.scrollSinceInteraction = false
    this.swallowedScrollEnd = false
  }

  scheduleFallbackSettle() {
    clearTimeout(this.scrollTimer)
    if (this.pointerActive || this.touchActive || this.submitting) return
    if (this.scrollEndSupported && this.scrollSinceInteraction && !this.swallowedScrollEnd) return
    this.scrollTimer = setTimeout(() => this.settle(), 120)
  }

  settle() {
    clearTimeout(this.scrollTimer)
    if (this.submitting || this.pointerActive || this.touchActive) return
    const distances = this.cardTargets.map((_, index) => Math.abs(this.railTarget.scrollLeft - this.offsetFor(index)))
    const index = distances.indexOf(Math.min(...distances))
    // No timer may grant ownership to a half-visible card.
    if (distances[index] > 1) return
    this.moving = false
    this.scrollSinceInteraction = false
    this.swallowedScrollEnd = false
    delete this.element.dataset.pickerMoving
    this.activate(index)
    this.dispatch("settled", { detail: { index, key: this.activeCard.querySelector("input").value } })
  }

  offsetFor(index) { return this.cardTargets[index].offsetLeft }

  activate(index) {
    this.index = index
    this.cardTargets.forEach((card, position) => {
      const active = position === index
      const input = card.querySelector("input")
      input.checked = active
      input.disabled = !active
      card.toggleAttribute("inert", !active)
      if (active) card.removeAttribute("aria-hidden")
      else card.setAttribute("aria-hidden", "true")
      card.querySelector(".ve-purchase-reason").textContent = input.dataset.reason
      card.querySelector(".ve-purchase-confirm").disabled = !active || Boolean(input.dataset.reason)
      card.querySelector('[aria-label="Previous model"]').disabled = !active || position === 0
      card.querySelector('[aria-label="Next model"]').disabled = !active || position === this.cardTargets.length - 1
    })
  }
}
